import { useEffect, useState } from 'react';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { getConsent, useConsentHandler } from '@/lib/consent';
import {
  disablePodscribe,
  enablePodscribe,
  flushPodscribeEvents,
  isPodscribeEnabled,
} from '@/lib/analytics/podscribe/client';
import { initialisePodscribe, podscribeView } from '@/lib/analytics/podscribe/events';
import { usePodscribeEnabled } from '@/lib/analytics/podscribe/usePodscribeEnabled';

const PODSCRIBE_SCRIPT_URL = 'https://d34r8q7sht0t9k.cloudfront.net/tag.js';

const PodscribeScript = () => {
  const router = useRouter();
  const podscribeEnabled = usePodscribeEnabled();
  const [scriptReady, setScriptReady] = useState(false);

  useConsentHandler('podscribe', ({ allowed }) => {
    if (allowed) {
      enablePodscribe();
      return;
    }

    const wasEnabled = isPodscribeEnabled();
    disablePodscribe();

    // tag.js cannot be unloaded in place; reload drops the runtime the same way GTM does.
    // Next tick so a same-tick re-accept can keep the session without a reload.
    if (wasEnabled && typeof window !== 'undefined') {
      window.setTimeout(() => {
        if (!getConsent().allowed) {
          window.location.reload();
        }
      }, 0);
    }
  });

  useEffect(() => {
    if (!podscribeEnabled) {
      setScriptReady(false);
    }
  }, [podscribeEnabled]);

  useEffect(() => {
    if (!podscribeEnabled || !scriptReady) {
      return;
    }

    router.events.on('routeChangeComplete', podscribeView);
    return () => router.events.off('routeChangeComplete', podscribeView);
  }, [podscribeEnabled, scriptReady, router.events]);

  if (!podscribeEnabled) {
    return null;
  }

  const handleReady = () => {
    if (!enablePodscribe()) {
      return;
    }

    initialisePodscribe();
    podscribeView();
    flushPodscribeEvents();
    setScriptReady(true);
  };

  return (
    <Script
      id="podscribe-capture"
      src={PODSCRIBE_SCRIPT_URL}
      strategy="afterInteractive"
      onReady={handleReady}
    />
  );
};

export default PodscribeScript;
